import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { unitCategoryApi } from '../../services/admin/adminApi';
import { toast } from 'sonner';
import type {
  UnitCategoryCreateRequest,
  UnitCategoryUpdateRequest,
  UnitFormulaCreateRequest,
} from '../../types/admin';

/**
 * React Query hook for fetching all unit categories
 * @returns Query result with unit category list
 */
export function useUnitCategories() {
  return useQuery({
    queryKey: ['admin', 'unitCategories'],
    queryFn: () => unitCategoryApi.list(),
    select: (response) => response.data,
  });
}

/**
 * React Query hook for fetching single unit category with formulas
 * @param id - Unit category ID
 * @returns Query result with category details and formulas
 */
export function useUnitCategory(id: number) {
  return useQuery({
    queryKey: ['admin', 'unitCategories', id],
    queryFn: () => unitCategoryApi.get(id),
    select: (response) => response.data,
    enabled: !!id,
  });
}

/**
 * React Query mutation hook for creating new unit category
 * Invalidates unit category list on success
 * @returns Mutation result for category creation
 */
export function useCreateUnitCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UnitCategoryCreateRequest) =>
      unitCategoryApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'unitCategories'] });
      toast.success('Unit category created successfully');
    },
    onError: (error: any) => {
      console.error('Failed to create unit category:', error);
      toast.error(error?.response?.data?.error?.message || error.message || 'Failed to create unit category');
    },
  });
}

/**
 * React Query mutation hook for updating unit category
 * Invalidates unit category list on success
 * @returns Mutation result for category update
 */
export function useUpdateUnitCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<UnitCategoryUpdateRequest> }) =>
      unitCategoryApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'unitCategories'] });
      toast.success('Unit category updated successfully');
    },
    onError: (error: any) => {
      console.error('Failed to update unit category:', error);
      toast.error(error?.response?.data?.error?.message || error.message || 'Failed to update unit category');
    },
  });
}

/**
 * React Query mutation hook for bulk deleting unit categories
 * Invalidates unit category list on success
 * @returns Mutation result for category deletion
 */
export function useDeleteUnitCategories() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ucatIds: number[]) => unitCategoryApi.delete(ucatIds),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'unitCategories'] });
      toast.success(`Successfully deleted ${variables.length} unit ${variables.length === 1 ? 'category' : 'categories'}`);
    },
    onError: (error: any) => {
      console.error('Failed to delete unit categories:', error);
      toast.error(error?.response?.data?.error?.message || error.message || 'Failed to delete unit categories');
    },
  });
}

// ============================================================================
// Unit Formula Hooks
// ============================================================================

/**
 * React Query mutation hook for creating new unit formula
 * Invalidates specific category query on success
 * @returns Mutation result for formula creation
 */
export function useCreateUnitFormula() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ categoryId, data }: { categoryId: number; data: UnitFormulaCreateRequest }) =>
      unitCategoryApi.createFormula(categoryId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'unitCategories', variables.categoryId],
      });
      toast.success('Unit formula created successfully');
    },
    onError: (error: any) => {
      console.error('Failed to create unit formula:', error);
      toast.error(error?.response?.data?.error?.message || error.message || 'Failed to create unit formula');
    },
  });
}

/**
 * React Query mutation hook for updating unit formula
 * Invalidates all unit category queries on success
 * @returns Mutation result for formula update
 */
export function useUpdateUnitFormula() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<UnitFormulaCreateRequest> }) =>
      unitCategoryApi.updateFormula(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'unitCategories'] });
      toast.success('Unit formula updated successfully');
    },
    onError: (error: any) => {
      console.error('Failed to update unit formula:', error);
      toast.error(error?.response?.data?.error?.message || error.message || 'Failed to update unit formula');
    },
  });
}

/**
 * React Query mutation hook for bulk deleting unit formulas
 * Invalidates all unit category queries on success
 * @returns Mutation result for formula deletion
 */
export function useDeleteUnitFormulas() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (untfrmIds: number[]) => unitCategoryApi.deleteFormulas(untfrmIds),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'unitCategories'] });
      toast.success(`Successfully deleted ${variables.length} unit ${variables.length === 1 ? 'formula' : 'formulas'}`);
    },
    onError: (error: any) => {
      console.error('Failed to delete unit formulas:', error);
      toast.error(error?.response?.data?.error?.message || error.message || 'Failed to delete unit formulas');
    },
  });
}
